// Central error logger: reports to Sentry, keeps the last 50 errors in
// localStorage, and (outside tests) mirrors them to RTDB `appErrors` so the
// team can query recent failures from the app. Every catch path should call it.
import * as Sentry from '@sentry/react';
import { rtdb } from '@gitextrade/shared';

const STORAGE_KEY = 'gx_app_errors';
const MAX_STORED = 50;

export interface ErrorLog {
  timestamp: number;
  message: string;
  name: string;
  stack: string;
  url: string;
  context?: string;
  componentStack?: string;
}

export async function logError(
  error: unknown,
  context?: string,
  componentStack?: string,
): Promise<void> {
  const err = error instanceof Error ? error : new Error(String(error));
  console.error(context ? `[${context}] ${err.message}` : err.message, err);

  Sentry.captureException(err, { extra: { context, componentStack } });

  const entry: ErrorLog = {
    timestamp: Date.now(),
    message: err.message,
    name: err.name,
    stack: err.stack ?? '',
    url: typeof window !== 'undefined' ? window.location.href : '',
    context,
    componentStack,
  };

  if (typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const list: ErrorLog[] = raw ? (JSON.parse(raw) as ErrorLog[]) : [];
      list.push(entry);
      while (list.length > MAX_STORED) list.shift();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      console.error('Failed to persist error to localStorage', e);
    }
  }

  if (import.meta.env.MODE !== 'test' && import.meta.env.VITE_FB_API_KEY) {
    try {
      const { db } = await import('../config/firebase');
      if (db) {
        const { ref, push, set } = await import('firebase/database');
        await set(push(ref(db, rtdb.appErrors())), entry);
      }
    } catch (e) {
      console.error('Failed to mirror error to Firebase', e);
    }
  }
}
